import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Typography,
  useMediaQuery
} from "@mui/material";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FileRejection, useDropzone } from "react-dropzone";
import { ErrorResponse } from "../../../models/ErrorResponse";
import DownloadIcon from '@mui/icons-material/Download';
import FilePresentIcon from '@mui/icons-material/FilePresent';

const MAXSIZE:number = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/jpg",
  "image/bmp",
  "image/tiff",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
interface DropzoneButtonDisguiseProps {
  cbFileAccepted: (afiles: Array<File>) => void;
  validation: boolean;
  setErrors: (errors:ErrorResponse) => void;
}

const DropzoneButtonDisguise = (props: DropzoneButtonDisguiseProps) => {
  const {cbFileAccepted,setErrors} = props;
  const onDrop = useCallback(
    (acceptedFiles:Array<File>) => {
      cbFileAccepted(acceptedFiles);
    },
    [cbFileAccepted]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: props.validation ? ALLOWED_FILE_TYPES : undefined,
    maxFiles: props.validation ? 1 : undefined,
    maxSize: props.validation ? MAXSIZE : undefined,
    onDropRejected: (x:Array<FileRejection>) => {
      setErrors({
        errortitle: "Please choose another file.",
        error:
          x[0].errors[0].message.indexOf(MAXSIZE.toString()) > 0
            ? x[0].errors[0].message
            : "File is larger than 5MB",
      });
    },
  });

  return (
    <Button {...getRootProps()} variant="outlined">
      <input {...getInputProps()} />
      Upload
    </Button>
  );
};

const downloadShim = (file: File) => {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", file.name);

  document.body.appendChild(link);

  // Start download
  link.click();

  // Clean up and remove the link
  link!.parentNode!.removeChild(link);
};

export interface UploadWidgetProps {
  sectionName: string;
  cbSetFiles: (e: Array<File>) => void;
  validation: boolean;
  setErrors: Dispatch<SetStateAction<ErrorResponse>>;
}

interface ToBeUploadedFiles {
  file: File;
  uploaded: boolean;
}

const UploadWidget = (props: UploadWidgetProps) => {
  const [files, setFiles] = useState<Array<ToBeUploadedFiles>>([]);

  const uploadWrapper = (afiles: Array<File>) => {
    setFiles(afiles.map((file: File) => ({ file: file, uploaded: false })));
    props.cbSetFiles(afiles);
  };

  const matches = useMediaQuery('(max-width:900px)');

  return (
    <Grid
      item
      sx={{ p: "12px", border: "1px solid #EDEDED", borderRadius: "8px", mb: "12px", display: "flex", width: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", position: "relative", width: "100%" }}>
        <Box sx={(theme) => ({ 
            display: "flex", mt: "8px", 
            flex: "0 0 40%",
            [theme.breakpoints.down('md')]: {
              flex: '0 0 100%',
            }
          })}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                backgroundColor: "rgba(0, 109, 166, 0.11)",
                borderRadius: "8px",
                p: "6px 8px",
                mr: "8px"
              }}
            >
              <FilePresentIcon/>
            </Box>
            <Typography
              sx={(theme) => ({ whiteSpace: "pre-wrap", fontSize: "20px", color: theme.palette.primary.main })}>
              {props.sectionName}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={(theme) => ({ 
            display: "flex", flexDirection: "column", mt: "8px", 
            flex: "0 0 20%",
            [theme.breakpoints.down('md')]: {
              flex: '0 0 50%',
            },
            [theme.breakpoints.down('sm')]: {
              flex: '0 0 100%',
            }
          })}>
          <Typography sx={{ fontSize: "14px" }}>
            <strong>File Name</strong>
          </Typography>
          {files?.map((file: ToBeUploadedFiles) => (<Typography key={file.file.name}>{file.file.name}</Typography>))}
        </Box>

        <Box sx={(theme) => ({ 
            display: "flex", flexDirection: "column", mt: "8px",
            flex: "0 0 20%",
            [theme.breakpoints.down('md')]: {
              flex: '0 0 50%',
            },
            [theme.breakpoints.down('sm')]: {
              flex: '0 0 100%',
              ml: "0"
            }
          })}>
          <Typography sx={{ fontSize: "14px" }}>
            <strong>Status</strong>
          </Typography>
          {files?.map((file: ToBeUploadedFiles) => (<Typography key={file.file.name}>{file?.uploaded ? "Uploaded" : "Queued for Upload"}</Typography>))}
        </Box>

        {files?.map((file: ToBeUploadedFiles, index: number) => (
          <Box sx={(theme) => 
            matches 
              ? { display: "flex", alignItems: "center", position: "absolute", bottom: "0"} 
              : { mt: "8px", flex: "0 0 10%",
              [theme.breakpoints.down('md')]: {
                flex: '0 0 50%',
              },
              [theme.breakpoints.down('sm')]: {
                flex: '0 0 100%',
              }}} key={file.file.name}>
            <ButtonGroup>
              <IconButton
                aria-label="view"
                onClick={() => {
                  downloadShim(file.file);
                }}
                sx={{ mr: "8px" }}
              >
                <DownloadIcon color="primary" />
              </IconButton>
              <IconButton
                aria-label="remove"
                onClick={() => {
                  props.cbSetFiles(files.filter((f, i) => i !== index).map(file=>file.file));
                  setFiles(files.filter((f, i) => i !== index));
                }}
              >
                <DeleteOutlineIcon color="primary" />
              </IconButton>
            </ButtonGroup>
          </Box>
        ))}

        <Box sx={(theme) => 
            matches 
              ? { mt: "8px", display: "flex", justifyContent: "flex-end", 
              flex: "0 0 auto",
              [theme.breakpoints.down('md')]: {
                flex: '0 0 100%',
              }} 
              : { mt: "8px", display: "flex", justifyContent: "flex-end",
              flex: "0 0 auto",
              [theme.breakpoints.down('md')]: {
                flex: '0 0 100%',
              }}}>
          <DropzoneButtonDisguise
            cbFileAccepted={uploadWrapper}
            validation={props.validation}
            setErrors={props.setErrors}
          />
        </Box>
      </Box>
    </Grid>
  );
};

export default UploadWidget;
